// TRUST-REIMBURSEMENTS-1 (2026-06-02) — Reimbursement / funding-request workflow.
// Lives in ClaimDetail → Financials → Reimbursements (the labeled seam from
// CLAIM-WORKSPACE-UX-1). Three layers: request → recipient split (by slip %) →
// receipts. The claim wall + scope are enforced server-side; canOnClaim() here
// is strictly UI hinting (hide controls a user can't use — never dead buttons).
//
// Caps (claim-level, from /me/permissions.claim_capabilities):
//   create_reimbursement  — create Draft, edit own Draft, Send.
//   approve_reimbursement  — edit-after-send, void, record receipt, recompute,
//                            close. (accountant + system_admin only)
import { useState, useEffect, useCallback } from "react";
import { apiFetch, fmt, canOnClaim, getUser } from "../api";
import { StatusPill as UiStatusPill } from "./ui";
import EmailRequestModal from "./EmailRequestModal";

// Backend-generated statement/package download: fetch the streamed file with
// the JWT (a plain <a href> can't carry the bearer header), then anchor-download
// it using the server's Content-Disposition filename.
export async function downloadReimbExport(path) {
  const res = await apiFetch(path, { raw: true });
  if (!res.ok) throw new Error(await res.text());
  const disp = res.headers.get("Content-Disposition") || "";
  const m = /filename="?([^"]+)"?/.exec(disp);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = m ? m[1] : "reimbursement";
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

export const TYPE_LABEL = {
  payment_reimbursement: "Payment reimbursement",
  bill_funding: "Bill funding",
  reserve_estimate: "Reserve estimate",
  manual_other: "Manual / other",
};

// TRUST-REIMBURSEMENTS-4 — allocation modes. `already_split` is the fix for the
// prior failure where pre-split rows were split again: it puts the WHOLE amount
// on one named payor and never re-divides.
export const ALLOC_MODE_LABEL = {
  auto_slip: "Auto-split by slip",
  already_split: "Already split / do not split",
  manual: "Manual allocation",
};
export const ALLOC_MODE_HINT = {
  auto_slip: "Atticus divides the amount across the underwriters on this claim's slip by participation %.",
  already_split: "The full amount goes to one named payor — used when the request was already divided upstream. No further splitting.",
  manual: "Enter each recipient and amount by hand. The rows must total the request amount.",
};

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const sumAlloc = (rows) => round2((rows || []).reduce((a, r) => a + (Number(r.allocated_amount) || 0), 0));

// Shared recipient-split editor used by both create and edit-split. Rows carry
// { display_name, allocated_amount, company_id?, underwriter_party_id?, participation_pct?, is_lead? }.
export function AllocationEditor({ mode, total, underwriters, eligible, rows, setRows }) {
  const lbl = { fontSize: 12, color: "var(--text2)", fontWeight: 600, display: "block", marginBottom: 4 };
  const inp = { width: "100%", padding: "6px 9px", fontSize: 13, border: "1px solid var(--border)", borderRadius: 6 };
  const elig = (eligible && eligible.length) ? eligible : (underwriters || []);
  const uwByName = {};
  elig.forEach(u => { uwByName[(u.display_name || "").trim().toLowerCase()] = u; });

  const setName = (i, name) => {
    const next = rows.slice();
    const u = uwByName[(name || "").trim().toLowerCase()];
    next[i] = { ...next[i], display_name: name,
      company_id: u ? u.company_id : null, underwriter_party_id: u ? u.underwriter_party_id : null,
      participation_pct: u ? u.participation_pct : null, is_lead: u ? u.is_lead : false,
      recipient_kind: u ? u.recipient_kind : null };
    setRows(next);
  };
  const setAmt = (i, amt) => { const next = rows.slice(); next[i] = { ...next[i], allocated_amount: amt }; setRows(next); };
  const addRow = () => setRows([...(rows || []), { display_name: "", allocated_amount: "" }]);
  const rmRow = (i) => setRows(rows.filter((_, j) => j !== i));
  const seedFromSlip = () => {
    const us = underwriters || [];
    if (!us.length) return;
    const tot = us.reduce((a, u) => a + (Number(u.participation_pct) || 0), 0);
    let acc = 0;
    const next = us.map((u, idx) => {
      let amt = tot > 0 ? round2((Number(total) || 0) * (Number(u.participation_pct) || 0) / tot)
                        : round2((Number(total) || 0) / us.length);
      acc += amt; return { display_name: u.display_name, company_id: u.company_id, underwriter_party_id: u.underwriter_party_id,
        participation_pct: u.participation_pct, is_lead: u.is_lead, allocated_amount: amt };
    });
    if (next.length) { // lead (or first) absorbs the rounding remainder
      const li = Math.max(0, next.findIndex(r => r.is_lead));
      next[li].allocated_amount = round2(next[li].allocated_amount + ((Number(total) || 0) - acc));
    }
    setRows(next);
  };
  // Manual mode helper: make the LAST row absorb whatever's left so the split
  // balances to the request total in one click (fixes "manual won't let me save").
  const balanceLast = () => {
    if (!rows.length) { setRows([{ display_name: "", allocated_amount: round2(Number(total) || 0) }]); return; }
    const others = rows.slice(0, -1).reduce((a, r) => a + (Number(r.allocated_amount) || 0), 0);
    const next = rows.slice();
    next[next.length - 1] = { ...next[next.length - 1], allocated_amount: round2((Number(total) || 0) - others) };
    setRows(next);
  };

  if (mode === "auto_slip") {
    return (
      <div style={{ fontSize: 12, color: "var(--text3)", border: "1px dashed var(--border)", borderRadius: 6, padding: "8px 10px" }}>
        {(underwriters || []).length === 0
          ? "⚠ No underwriters on this claim's slip. Add recipients manually, or this request can't be sent until the slip is populated."
          : `Will split ${fmt.money(total)} across ${underwriters.length} underwriter(s) by slip participation %.`}
      </div>
    );
  }

  const allocated = sumAlloc(rows);
  const balanced = Math.abs(allocated - (Number(total) || 0)) <= 0.01;
  const names = elig.map(u => u.display_name);
  const single = mode === "already_split";

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
        <label style={lbl}>{single ? "Payor (one recipient)" : "Recipients"}</label>
        {!single && underwriters && underwriters.length > 0 &&
          <button type="button" className="btn btn-outline btn-sm" style={{ padding: "2px 8px", fontSize: 11 }} onClick={seedFromSlip}>Seed from slip</button>}
      </div>
      <datalist id="uw-names">{names.map((n, i) => <option key={i} value={n} />)}</datalist>
      {(single ? rows.slice(0, 1) : rows).map((r, i) => (
        <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6, alignItems: "center" }}>
          <input style={{ ...inp, flex: 2 }} list="uw-names" placeholder="Underwriter / payor name"
            value={r.display_name || ""} onChange={e => setName(i, e.target.value)} />
          <input style={{ ...inp, flex: 1, fontFamily: "JetBrains Mono,monospace" }} type="number" step="0.01"
            placeholder="0.00"
            value={single ? round2(total) : (r.allocated_amount ?? "")}
            readOnly={single}
            onChange={e => setAmt(i, e.target.value)} />
          {!single && <button type="button" className="btn btn-outline btn-sm" style={{ padding: "2px 8px", fontSize: 11, color: "#991b1b" }} onClick={() => rmRow(i)} disabled={rows.length <= 1}>✕</button>}
        </div>
      ))}
      {single && (
        <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 2 }}>
          Amount is locked to the request total above. To split across multiple payors, choose <strong>Manual allocation</strong>.
        </div>
      )}
      {!single && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <button type="button" className="btn btn-outline btn-sm" style={{ padding: "2px 10px", fontSize: 11 }} onClick={addRow}>+ Add recipient</button>
          {!balanced && (Number(total) || 0) > 0 &&
            <button type="button" className="btn btn-outline btn-sm" style={{ padding: "2px 10px", fontSize: 11 }} onClick={balanceLast}>Balance to total</button>}
        </div>
      )}
      {!single && (
        <div style={{ fontSize: 12, marginTop: 8, fontFamily: "JetBrains Mono,monospace", color: balanced ? "#166534" : "#991b1b" }}>
          Allocated {fmt.money(allocated)} / {fmt.money(total)} {balanced ? "✓ balanced" : `· off by ${fmt.money(round2((Number(total)||0) - allocated))}`}
        </div>
      )}
    </div>
  );
}

const STATUS_COLORS = {
  "Draft":              { background: "#f1f5f9", color: "#475569" },
  "Sent":               { background: "#dbeafe", color: "#1e40af" },
  "Partially Received": { background: "#fef3c7", color: "#92400e" },
  "Received":           { background: "#dcfce7", color: "#166534" },
  "Closed":             { background: "#e2e8f0", color: "#334155" },
  "Void":               { background: "#fee2e2", color: "#991b1b" },
};

const FR_KIND_LABEL = { underwriter: "Underwriters", assured: "Assured", broker: "Broker", tpa: "TPA", other: "Other" };
const frKinds = (k) => (k ? String(k).split(", ").map((x) => FR_KIND_LABEL[x] || x).join(", ") : "—");

export function StatusPill({ status }) {
  return <UiStatusPill status={status} />;
}

const HEALTH_STYLE = {
  "Reconciled":   { background: "#dcfce7", color: "#166534" },
  "Outstanding":  { background: "#fef3c7", color: "#92400e" },
  "Needs Review": { background: "#fee2e2", color: "#991b1b" },
  "No Activity":  { background: "#f1f5f9", color: "#475569" },
};
function HealthBadge({ health }) {
  const st = HEALTH_STYLE[health.health] || HEALTH_STYLE["No Activity"];
  return (
    <div style={{ minWidth: 130 }}>
      <div style={{ fontSize: 11, color: "var(--text3)", textTransform: "uppercase", letterSpacing: 0.4 }}>Health</div>
      <div style={{ marginTop: 2, display: "flex", alignItems: "center", gap: 6 }}>
        <span className="badge" style={{ ...st, fontSize: 11, padding: "2px 8px", fontWeight: 700 }}>{health.health}</span>
        {health.needs_review > 0 && <span style={{ fontSize: 11, color: "#991b1b" }}>{health.needs_review} to review</span>}
      </div>
    </div>
  );
}

function Metric({ label, value, color }) {
  return (
    <div style={{ flex: 1, minWidth: 110 }}>
      <div style={{ fontSize: 11, color: "var(--text3)", textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 700, fontFamily: "JetBrains Mono,monospace", marginTop: 1, color: color || "inherit" }}>{value}</div>
    </div>
  );
}

export default function ReimbursementsPanel({ claim, claimId, onWorkChanged }) {
  const user = getUser();
  const canCreate = canOnClaim("create_reimbursement", claim, user);
  const canApprove = canOnClaim("approve_reimbursement", claim, user);

  const [data, setData] = useState({ requests: [], summary: null });
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [selId, setSelId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    apiFetch(`/claims/${claimId}/reimbursements`)
      .then(d => { setData(d || { requests: [], summary: null }); setErr(null); })
      .catch(e => setErr(String(e.message || e)))
      .finally(() => setLoading(false));
    apiFetch(`/claims/${claimId}/reimbursement-health`).then(setHealth).catch(() => setHealth(null));
  }, [claimId]);
  useEffect(() => { load(); }, [load]);

  // Funds-request mutations feed the ribbon (Funds Requested/Received) and the
  // Overview financial tiles — reload locally AND bump the workspace counter.
  const changed = () => { load(); onWorkChanged && onWorkChanged(); };

  if (loading) return <div className="empty-state" style={{ padding: 24 }}>Loading reimbursements…</div>;

  const s = data.summary || {};
  return (
    <div>
      {/* Summary strip */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ display: "flex", gap: 24, flexWrap: "wrap", alignItems: "center", padding: "14px 20px" }}>
          <Metric label="Requested" value={fmt.money(s.total_requested || 0)} />
          <Metric label="Received" value={fmt.money(s.total_received || 0)} color="var(--green, #166534)" />
          <Metric label="Outstanding" value={fmt.money(s.total_outstanding || 0)} color={(s.total_outstanding || 0) > 0 ? "var(--navy)" : "inherit"} />
          <Metric label="Open" value={s.open_count || 0} />
          {health && <HealthBadge health={health} />}
          <div style={{ flex: 1 }} />
          {canCreate && !showCreate && (
            <button className="btn btn-primary btn-sm" onClick={() => { setShowCreate(true); setSelId(null); }}>+ New Request</button>
          )}
        </div>
      </div>

      {err && <div className="card" style={{ marginBottom: 16 }}><div className="card-body" style={{ color: "var(--red)", fontSize: 13 }}>{err}</div></div>}

      {showCreate && (
        <CreateForm claimId={claimId} onClose={() => setShowCreate(false)} onSaved={(r) => { setShowCreate(false); changed(); setSelId(r?.id || null); }} />
      )}

      {/* Request table */}
      <div className="card">
        <div className="section-header"><span className="section-title">Funds Requests ({(data.requests || []).length})</span></div>
        <div className="card-body" style={{ padding: 0 }}>
          {(data.requests || []).length === 0 ? (
            <div className="empty-state" style={{ padding: 24 }}>
              No reimbursement requests yet.{canCreate ? " Use “+ New Request” to start one." : ""}
            </div>
          ) : (
            <table style={{ width: "100%", fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: "left", color: "var(--text3)", fontSize: 11, textTransform: "uppercase" }}>
                  <th style={{ padding: "8px 12px" }}>Type</th>
                  <th style={{ padding: "8px 12px" }}>Basis</th>
                  <th style={{ padding: "8px 12px", textAlign: "right" }}>Requested</th>
                  <th style={{ padding: "8px 12px", textAlign: "right" }}>Received</th>
                  <th style={{ padding: "8px 12px", textAlign: "right" }}>Outstanding</th>
                  <th style={{ padding: "8px 12px" }}>To</th>
                  <th style={{ padding: "8px 12px" }}>Status</th>
                  <th style={{ padding: "8px 12px" }}>Age</th>
                  <th style={{ padding: "8px 12px" }}>Date</th>
                </tr>
              </thead>
              <tbody>
                {data.requests.map(r => (
                  <tr key={r.id} onClick={() => { setSelId(selId === r.id ? null : r.id); setShowCreate(false); }}
                      style={{ cursor: "pointer", borderTop: "1px solid var(--border)", background: selId === r.id ? "var(--bg2, #f8fafc)" : "transparent" }}>
                    <td style={{ padding: "8px 12px" }}>{TYPE_LABEL[r.request_type] || r.request_type}</td>
                    <td style={{ padding: "8px 12px", color: "var(--text3)", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.basis_description || "—"}</td>
                    <td style={{ padding: "8px 12px", textAlign: "right", fontFamily: "JetBrains Mono,monospace" }}>{fmt.money(r.amount_requested)}</td>
                    <td style={{ padding: "8px 12px", textAlign: "right", fontFamily: "JetBrains Mono,monospace" }}>{fmt.money(r.received_total)}</td>
                    <td style={{ padding: "8px 12px", textAlign: "right", fontFamily: "JetBrains Mono,monospace", color: (r.outstanding_total || 0) > 0 && r.status !== "Void" ? "var(--navy)" : "var(--text3)" }}>{fmt.money(r.outstanding_total)}</td>
                    <td style={{ padding: "8px 12px", fontSize: 11, color: "var(--text2)" }}>{frKinds(r.recipient_kinds)}</td>
                    <td style={{ padding: "8px 12px" }}><StatusPill status={r.status} /></td>
                    <td style={{ padding: "8px 12px", fontSize: 11, color: "var(--text3)", whiteSpace: "nowrap" }}>{r.age_days != null ? `${r.age_days}d` : "—"}{r.next_follow_up_at ? <span title={`Next follow-up ${fmt.date(r.next_follow_up_at)}`} style={{ marginLeft: 5, color: "#b45309" }}>⚑</span> : null}</td>
                    <td style={{ padding: "8px 12px", color: "var(--text3)", whiteSpace: "nowrap" }}>{fmt.date(r.request_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {selId && (
        <RequestDetail requestId={selId} canCreate={canCreate} canApprove={canApprove}
          onChanged={changed} onClose={() => setSelId(null)} />
      )}
    </div>
  );
}

// ── Create form ──────────────────────────────────────────────────────────────
function CreateForm({ claimId, onClose, onSaved }) {
  const [type, setType] = useState("manual_other");
  const [amount, setAmount] = useState("");
  const [desc, setDesc] = useState("");
  const [basisSource, setBasisSource] = useState(null);
  const [basisRefId, setBasisRefId] = useState(null);
  const [basisItems, setBasisItems] = useState([]);
  const [allocMode, setAllocMode] = useState("auto_slip");
  const [rows, setRows] = useState([{ display_name: "", allocated_amount: "" }]);
  const [underwriters, setUnderwriters] = useState([]);
  const [eligible, setEligible] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    apiFetch(`/claims/${claimId}/slip-underwriters`)
      .then(d => { setUnderwriters(d.underwriters || []); setEligible(d.eligible || d.underwriters || []); })
      .catch(() => setUnderwriters([]));
  }, [claimId]);

  useEffect(() => {
    setBasisItems([]); setBasisRefId(null); setBasisSource(null);
    if (type === "manual_other") return;
    apiFetch(`/claims/${claimId}/reimbursement-basis?type=${type}`)
      .then(d => {
        setBasisSource(d.basis_source || null);
        setBasisItems(d.items || []);
        if (type === "reserve_estimate" && d.total_reserve != null) {
          setAmount(String(d.total_reserve));
          setDesc(`Reserve estimate — current Total Reserve ${fmt.money(d.total_reserve)}`);
        }
      })
      .catch(() => {});
  }, [type, claimId]);

  const pickBasis = (item) => {
    setBasisRefId(item.id);
    if (type === "payment_reimbursement") {
      setAmount(String(item.amount));
      setDesc(`Reimbursement of payment to ${item.payee || "—"} (${fmt.date(item.payment_date)})`);
    } else if (type === "bill_funding") {
      setAmount(String(item.amount));
      setDesc(`Funding for bill ${item.invoice_number || ""} — ${item.payee || "—"}`.trim());
    }
  };

  const amt = parseFloat(amount) || 0;
  const buildRecipients = () => {
    if (allocMode === "already_split") {
      const r = rows[0] || {};
      return [{ ...r, allocated_amount: round2(amt) }];
    }
    return (rows || []).filter(r => (r.display_name || "").trim() || Number(r.allocated_amount))
      .map(r => ({ ...r, allocated_amount: round2(r.allocated_amount) }));
  };
  const recips = buildRecipients();
  const allocOk = allocMode === "auto_slip"
    ? underwriters.length > 0
    : (recips.length > 0 &&
       (allocMode !== "already_split" || recips.length === 1) &&
       Math.abs(sumAlloc(recips) - amt) <= 0.01 &&
       recips.every(r => (r.display_name || "").trim()));

  const save = () => {
    if (!amt || amt <= 0) { setErr("Enter an amount greater than 0."); return; }
    if (!allocOk) {
      setErr(allocMode === "auto_slip"
        ? "No underwriters on the slip — switch to Manual or Already-split, or populate the slip."
        : "Recipient allocations must total the request amount, each with a name.");
      return;
    }
    setSaving(true); setErr(null);
    const body = { request_type: type, amount_requested: amt, basis_source: basisSource,
                   basis_ref_id: basisRefId, basis_description: desc, allocation_mode: allocMode };
    if (allocMode !== "auto_slip") body.recipients = recips;
    apiFetch(`/claims/${claimId}/reimbursements`, { method: "POST", body })
      .then(r => onSaved(r))
      .catch(e => setErr(String(e.message || e)))
      .finally(() => setSaving(false));
  };

  const lbl = { fontSize: 12, color: "var(--text2)", fontWeight: 600, display: "block", marginBottom: 4 };
  const inp = { width: "100%", padding: "7px 10px", fontSize: 13, border: "1px solid var(--border)", borderRadius: 6 };
  return (
    <div className="card" style={{ marginBottom: 16, borderColor: "var(--navy)" }}>
      <div className="section-header"><span className="section-title">New Funds Request</span></div>
      <div className="card-body" style={{ display: "grid", gap: 12 }}>
        <div className="grid-2" style={{ gap: 12 }}>
          <div>
            <label style={lbl}>Type</label>
            <select style={inp} value={type} onChange={e => { setType(e.target.value); setAmount(""); setDesc(""); }}>
              {Object.entries(TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>Amount requested</label>
            <input style={inp} type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" />
          </div>
        </div>

        {type !== "manual_other" && type !== "reserve_estimate" && (
          <div>
            <label style={lbl}>Basis ({basisSource || type})</label>
            {basisItems.length === 0
              ? <div style={{ fontSize: 12, color: "var(--text3)" }}>No eligible {basisSource || ""} records found — you can still enter a manual amount.</div>
              : (
                <div style={{ maxHeight: 160, overflow: "auto", border: "1px solid var(--border)", borderRadius: 6 }}>
                  {basisItems.map(it => (
                    <div key={it.id} onClick={() => pickBasis(it)}
                      style={{ padding: "6px 10px", fontSize: 12, cursor: "pointer", display: "flex", justifyContent: "space-between", gap: 10,
                               borderTop: "1px solid var(--border)", background: basisRefId === it.id ? "var(--bg2,#eff6ff)" : "transparent" }}>
                      <span>{type === "payment_reimbursement" ? `${it.payee || "—"} · ${fmt.date(it.payment_date)}` : `${it.payee || "—"} · ${it.invoice_number || it.status || ""}`}</span>
                      <span style={{ fontFamily: "JetBrains Mono,monospace" }}>{fmt.money(it.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
          </div>
        )}

        <div>
          <label style={lbl}>Description</label>
          <input style={inp} value={desc} onChange={e => setDesc(e.target.value)} placeholder="What this request covers" />
        </div>

        {/* Allocation mode — explicit, user-visible (no hidden magic) */}
        <div>
          <label style={lbl}>Allocation</label>
          <select style={inp} value={allocMode} onChange={e => {
            const m = e.target.value;
            setAllocMode(m);
            // Seed manual mode with one full-amount row so it starts balanced and
            // saveable (was starting blank → sum $0 ≠ amount → Save stuck disabled).
            if (m === "manual") {
              setRows(prev => {
                const blank = !prev.length || prev.every(r => !(r.display_name || "").trim() && !Number(r.allocated_amount));
                return blank ? [{ display_name: "", allocated_amount: round2(amt) }] : prev;
              });
            }
          }}>
            {Object.entries(ALLOC_MODE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <div style={{ fontSize: 11, color: "var(--text3)", margin: "4px 0 8px" }}>{ALLOC_MODE_HINT[allocMode]}</div>
          <AllocationEditor mode={allocMode} total={amt} underwriters={underwriters} eligible={eligible} rows={rows} setRows={setRows} />
        </div>

        {err && <div style={{ color: "var(--red)", fontSize: 12 }}>{err}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="btn btn-outline btn-sm" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={save} disabled={saving || !allocOk}>{saving ? "Saving…" : "Save Draft"}</button>
        </div>
      </div>
    </div>
  );
}

// ── Request detail (recipients + receipts + actions) ─────────────────────────
function RequestDetail({ requestId, canCreate, canApprove, onChanged, onClose }) {
  const [req, setReq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [underwriters, setUnderwriters] = useState([]);
  const [eligible, setEligible] = useState([]);
  const [genBusy, setGenBusy] = useState(null); // path currently downloading
  const [showEmail, setShowEmail] = useState(false); // email-send modal (Kuhrt 2026-07-07)

  const load = useCallback(() => {
    setLoading(true);
    apiFetch(`/reimbursements/${requestId}`)
      .then(d => { setReq(d); setErr(null); })
      .catch(e => setErr(String(e.message || e)))
      .finally(() => setLoading(false));
  }, [requestId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (req?.claim_id) apiFetch(`/claims/${req.claim_id}/slip-underwriters`).then(d => { setUnderwriters(d.underwriters || []); setEligible(d.eligible || d.underwriters || []); }).catch(() => {});
  }, [req?.claim_id]);

  const act = (fn) => { setBusy(true); setErr(null); fn().then(() => { load(); onChanged && onChanged(); }).catch(e => setErr(String(e.message || e))).finally(() => setBusy(false)); };
  const doSend = () => act(() => apiFetch(`/reimbursements/${requestId}/send`, { method: "POST", body: {} }));
  const doVoid = () => {
    const reason = window.prompt("Reason for voiding this request?");
    if (!reason) return;
    act(() => apiFetch(`/reimbursements/${requestId}/void`, { method: "POST", body: { reason } }));
  };
  const doRecompute = () => act(() => apiFetch(`/reimbursements/${requestId}/recipients/recompute`, { method: "POST", body: {} }));
  const doDelete = () => {
    if (!window.confirm("Delete this reimbursement request entirely? This removes the request and its underwriter split and cannot be undone. (Use Void instead if you want to keep the record.)")) return;
    setBusy(true); setErr(null);
    apiFetch(`/reimbursements/${requestId}`, { method: "DELETE" })
      .then(() => { onChanged && onChanged(); onClose && onClose(); })
      .catch(e => setErr(String(e.message || e)))
      .finally(() => setBusy(false));
  };

  // Statement/package generation (gated by create authority — the set that can
  // send a request). Generation never marks the request sent; reproducible at
  // any status. Server enforces the wall + cap regardless of this hint.
  const canGenerate = canCreate;
  const gen = (path) => {
    setGenBusy(path); setErr(null);
    downloadReimbExport(path).catch(e => setErr(String(e.message || e))).finally(() => setGenBusy(null));
  };

  if (loading) return <div className="card" style={{ marginTop: 16 }}><div className="card-body empty-state">Loading…</div></div>;
  if (!req) return null;

  const isDraft = req.status === "Draft";
  const isVoid = req.status === "Void" || req.status === "Closed";
  const canReceipt = canApprove && (req.status === "Sent" || req.status === "Partially Received" || req.status === "Received");
  // Hard-delete: approver-only, and only while no receipts exist (else Void).
  // H6 — also hidden once a QBO invoice exists: the hard delete destroys the recipient
  // rows (the only record of that invoice) and the backend now 409s. Void instead.
  const canDelete = canApprove && (req.receipts || []).length === 0 && !req.qbo_split_locked;
  // Edit-split: draft edits need create authority, post-send needs approve;
  // locked once a receipt exists (void + recreate to correct).
  const allocLocked = !!req.allocations_locked;
  // H6 / LD-REV-004 — a recipient carrying a live QBO Trust invoice locks the split:
  // Recompute / Edit Split delete the recipient rows, which are the only record of that
  // invoice, and a later Send to QBO would duplicate it. The backend refuses either way
  // (main._reimb_assert_no_qbo_invoices → 409); this says so before the click.
  const qboSplitLocked = !!req.qbo_split_locked;
  const qboLockNote = `${req.qbo_invoice_count || 0} recipient invoice${(req.qbo_invoice_count || 0) === 1 ? "" : "s"} already exist in QBO — the split is locked. Void the QBO invoice(s) in QuickBooks first, or void this request and recreate it.`;
  const canEditSplit = !isVoid && !allocLocked && !qboSplitLocked && (isDraft ? canCreate : canApprove);

  const th = { padding: "6px 10px", textAlign: "left", fontSize: 11, color: "var(--text3)", textTransform: "uppercase" };
  const td = { padding: "6px 10px", fontSize: 13 };
  const mono = { fontFamily: "JetBrains Mono,monospace" };
  return (
    <div className="card" style={{ marginTop: 16, borderColor: "var(--navy)" }}>
      <div className="section-header">
        <span className="section-title">{TYPE_LABEL[req.request_type] || req.request_type} · {fmt.money(req.amount_requested)} · <StatusPill status={req.status} /></span>
        <div style={{ display: "flex", gap: 6 }}>
          {isDraft && canCreate && <button className="btn btn-primary btn-sm" onClick={doSend} disabled={busy || req.allocation_valid === false} title={req.allocation_valid === false ? (req.allocation_message || "Allocations must balance before sending") : "Mark as sent"}>Send</button>}
          {canEditSplit && <button className="btn btn-outline btn-sm" onClick={() => { setShowEdit(!showEdit); setShowReceipt(false); }} disabled={busy}>{showEdit ? "Cancel Edit" : "Edit Split"}</button>}
          {canReceipt && <button className="btn btn-blue btn-sm" onClick={() => { setShowReceipt(!showReceipt); setShowEdit(false); }} disabled={busy}>{showReceipt ? "Cancel" : "Record Receipt"}</button>}
          {canApprove && !isVoid && !allocLocked && !qboSplitLocked && (req.allocation_mode || "auto_slip") === "auto_slip" && <button className="btn btn-outline btn-sm" onClick={doRecompute} disabled={busy} title="Re-split from the current slip">Recompute Split</button>}
          {qboSplitLocked && !isVoid && <span className="badge badge-amber" style={{ alignSelf: "center" }} title={qboLockNote}>🔒 SPLIT LOCKED · QBO</span>}
          {canApprove && req.status !== "Void" && <button className="btn btn-outline btn-sm" onClick={doVoid} disabled={busy} style={{ color: "#991b1b", borderColor: "#fecaca" }}>Void</button>}
          {canDelete && <button className="btn btn-outline btn-sm" onClick={doDelete} disabled={busy} style={{ color: "#fff", background: "#991b1b", borderColor: "#991b1b" }} title="Permanently delete (no receipts recorded)">Delete</button>}
          {canGenerate && <button className="btn btn-outline btn-sm" onClick={() => gen(`/reimbursements/${requestId}/statement/export?format=pdf`)} disabled={!!genBusy} title="Underwriter-facing request statement (all recipients), PDF">{genBusy && genBusy.endsWith("format=pdf") ? "…" : "Statement PDF"}</button>}
          {canGenerate && <button className="btn btn-outline btn-sm" onClick={() => gen(`/reimbursements/${requestId}/statement/export?format=xlsx`)} disabled={!!genBusy} title="Full request package workbook (XLSX)">{genBusy && genBusy.endsWith("format=xlsx") ? "…" : "Package XLSX"}</button>}
          {canGenerate && !isVoid && <button className="btn btn-primary btn-sm" onClick={() => setShowEmail(true)} disabled={busy || (isDraft && req.allocation_valid === false)} title={isDraft && req.allocation_valid === false ? (req.allocation_message || "Allocations must balance before sending") : "Email the statement (PDF attached, split table in the body). Sending a Draft marks it Sent."}>Email…</button>}
          <button className="btn btn-outline btn-sm" onClick={onClose}>Close</button>
        </div>
      </div>
      <div className="card-body">
        {err && <div style={{ color: "var(--red)", fontSize: 12, marginBottom: 10 }}>{err}</div>}
        {req.basis_description && <div style={{ fontSize: 13, color: "var(--text2)", marginBottom: 6 }}>{req.basis_description}</div>}
        <div style={{ fontSize: 12, color: "var(--text3)", marginBottom: 14 }}>
          Requested {fmt.money(req.allocated_total)} · Received {fmt.money(req.received_total)} · Outstanding {fmt.money(req.outstanding_total)}
          {req.created_by_name && ` · created by ${req.created_by_name}`}
          {req.sent_by_name && ` · sent by ${req.sent_by_name}`}
          {req.void_reason && ` · voided: ${req.void_reason}`}
        </div>

        {/* Allocation health: invalid split (blocks Send) or slip warning */}
        {req.allocation_valid === false && !isVoid && (
          <div style={{ fontSize: 12, color: "#991b1b", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 6, padding: "6px 10px", marginBottom: 8 }}>
            ⚠ {req.allocation_message || "Allocations don't balance."}{canEditSplit ? " Use Edit Split to fix." : ""}
          </div>
        )}
        {req.slip_warning && req.allocation_valid !== false && (
          <div style={{ fontSize: 12, color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, padding: "6px 10px", marginBottom: 8 }}>
            ⚠ {req.slip_warning}
          </div>
        )}
        {allocLocked && (
          <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 8 }}>Allocations are locked — receipts have been recorded. Void and recreate to correct the split.</div>
        )}
        {qboSplitLocked && !isVoid && (
          <div style={{ fontSize: 12, color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, padding: "6px 10px", marginBottom: 8 }}>
            🔒 {qboLockNote}
          </div>
        )}

        {showReceipt && <ReceiptForm requestId={requestId} recipients={req.recipients} onClose={() => setShowReceipt(false)} onSaved={() => { setShowReceipt(false); load(); onChanged && onChanged(); }} />}
        {showEdit && <EditSplitForm requestId={requestId} req={req} underwriters={underwriters} eligible={eligible}
          onClose={() => setShowEdit(false)} onSaved={() => { setShowEdit(false); load(); onChanged && onChanged(); }} />}

        <div style={{ fontWeight: 600, fontSize: 12, color: "var(--text2)", margin: "8px 0 4px" }}>
          Underwriter split · <span style={{ fontWeight: 500, color: "var(--text3)" }}>{ALLOC_MODE_LABEL[req.allocation_mode] || "Auto-split by slip"}</span>
        </div>
        <table style={{ width: "100%", marginBottom: 16 }}>
          <thead><tr><th style={th}>Underwriter</th><th style={{ ...th, textAlign: "right" }}>Slip %</th><th style={{ ...th, textAlign: "right" }}>Allocated</th><th style={{ ...th, textAlign: "right" }}>Received</th><th style={{ ...th, textAlign: "right" }}>Outstanding</th><th style={th}></th></tr></thead>
          <tbody>
            {(req.recipients || []).length === 0
              ? <tr><td style={{ ...td, color: "var(--text3)" }} colSpan={6}>No underwriters on the slip — request applies to the claim as a whole.</td></tr>
              : req.recipients.map(rc => {
                const out = Number(rc.allocated_amount || 0) - Number(rc.received_amount || 0);
                return (
                <tr key={rc.id} style={{ borderTop: "1px solid var(--border)" }}>
                  <td style={td}>{rc.display_name || "—"}{rc.is_lead && <span className="badge" style={{ marginLeft: 6, fontSize: 9, background: "#fee2e2", color: "#991b1b" }}>LEAD</span>}{rc.manual_override && <span className="badge" style={{ marginLeft: 6, fontSize: 9, background: "#fef3c7", color: "#92400e" }}>OVERRIDE</span>}</td>
                  <td style={{ ...td, ...mono, textAlign: "right" }}>{rc.participation_pct != null ? Number(rc.participation_pct).toFixed(2) + "%" : "—"}</td>
                  <td style={{ ...td, ...mono, textAlign: "right" }}>{fmt.money(rc.allocated_amount)}</td>
                  <td style={{ ...td, ...mono, textAlign: "right", color: (rc.received_amount || 0) > 0 ? "#166534" : "var(--text3)" }}>{fmt.money(rc.received_amount)}</td>
                  <td style={{ ...td, ...mono, textAlign: "right", color: out > 0 ? "var(--navy)" : "var(--text3)" }}>{fmt.money(out)}</td>
                  <td style={{ ...td, textAlign: "right" }}>{canGenerate && <button className="btn btn-outline btn-sm" style={{ padding: "2px 8px", fontSize: 11 }} disabled={!!genBusy} title="Recipient statement (PDF)" onClick={() => gen(`/reimbursements/${requestId}/statement/export?format=pdf&recipient_id=${rc.id}`)}>Statement</button>}</td>
                </tr>
                );
              })}
          </tbody>
        </table>

        <div style={{ fontWeight: 600, fontSize: 12, color: "var(--text2)", margin: "8px 0 4px" }}>Receipts ({(req.receipts || []).length})</div>
        {(req.receipts || []).length === 0
          ? <div className="empty-state" style={{ padding: 12, fontSize: 12 }}>No receipts recorded.</div>
          : (
            <table style={{ width: "100%" }}>
              <thead><tr><th style={th}>Date</th><th style={th}>Method</th><th style={th}>Reference</th><th style={{ ...th, textAlign: "right" }}>Amount</th><th style={th}>Recorded by</th></tr></thead>
              <tbody>
                {req.receipts.map(rc => (
                  <tr key={rc.id} style={{ borderTop: "1px solid var(--border)" }}>
                    <td style={td}>{fmt.date(rc.received_date)}</td>
                    <td style={td}>{rc.method || "—"}</td>
                    <td style={td}>{rc.reference || "—"}</td>
                    <td style={{ ...td, ...mono, textAlign: "right" }}>{fmt.money(rc.amount)}</td>
                    <td style={{ ...td, color: "var(--text3)" }}>{rc.recorded_by_name || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>
      {showEmail && (
        <EmailRequestModal
          endpoint={`/reimbursements/${requestId}/email`}
          title={`Email Funds Request FR-${requestId}`}
          defaultSubject={`Walters Northwest Funds Request FR-${requestId}${req.claim_number ? ` — Claim ${req.claim_number}` : ""}`}
          attachmentLabel={`Funds Request FR-${requestId} statement PDF`}
          recipientOptions={(req.recipients || []).map(r => ({ id: r.id, label: r.display_name || `Recipient ${r.id}` }))}
          onClose={() => setShowEmail(false)}
          onSent={() => { setShowEmail(false); load(); onChanged && onChanged(); }}
        />
      )}
    </div>
  );
}

// ── Edit-split form (change allocation mode / recipients) ────────────────────
function EditSplitForm({ requestId, req, underwriters, eligible, onClose, onSaved }) {
  const total = Number(req.amount_requested) || 0;
  const initRows = (req.recipients || []).map(r => ({
    display_name: r.display_name || "", allocated_amount: r.allocated_amount,
    company_id: r.company_id, underwriter_party_id: r.underwriter_party_id,
    participation_pct: r.participation_pct, is_lead: r.is_lead,
  }));
  const [mode, setMode] = useState(req.allocation_mode || "auto_slip");
  const [rows, setRows] = useState(initRows.length ? initRows : [{ display_name: "", allocated_amount: "" }]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const recips = mode === "already_split"
    ? [{ ...(rows[0] || {}), allocated_amount: round2(total) }]
    : (rows || []).filter(r => (r.display_name || "").trim() || Number(r.allocated_amount)).map(r => ({ ...r, allocated_amount: round2(r.allocated_amount) }));
  const ok = mode === "auto_slip"
    ? underwriters.length > 0
    : (recips.length > 0 && (mode !== "already_split" || recips.length === 1) &&
       Math.abs(sumAlloc(recips) - total) <= 0.01 && recips.every(r => (r.display_name || "").trim()));

  const save = () => {
    if (!ok) { setErr(mode === "auto_slip" ? "No underwriters on the slip to seed from." : "Allocations must total the request amount, each with a name."); return; }
    setSaving(true); setErr(null);
    const body = { allocation_mode: mode };
    if (mode !== "auto_slip") body.recipients = recips;
    apiFetch(`/reimbursements/${requestId}/recipients`, { method: "PUT", body })
      .then(() => onSaved())
      .catch(e => setErr(String(e.message || e)))
      .finally(() => setSaving(false));
  };

  const lbl = { fontSize: 12, color: "var(--text2)", fontWeight: 600, display: "block", marginBottom: 4 };
  const inp = { width: "100%", padding: "7px 10px", fontSize: 13, border: "1px solid var(--border)", borderRadius: 6 };
  return (
    <div className="card" style={{ marginBottom: 14, background: "var(--bg2,#f8fafc)" }}>
      <div className="card-body" style={{ display: "grid", gap: 10 }}>
        <div style={{ fontWeight: 600, fontSize: 13 }}>Edit allocation · {fmt.money(total)}</div>
        <div>
          <label style={lbl}>Allocation mode</label>
          <select style={inp} value={mode} onChange={e => setMode(e.target.value)}>
            {Object.entries(ALLOC_MODE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <div style={{ fontSize: 11, color: "var(--text3)", margin: "4px 0 8px" }}>{ALLOC_MODE_HINT[mode]}</div>
          <AllocationEditor mode={mode} total={total} underwriters={underwriters} eligible={eligible} rows={rows} setRows={setRows} />
        </div>
        {err && <div style={{ color: "var(--red)", fontSize: 12 }}>{err}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="btn btn-outline btn-sm" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={save} disabled={saving || !ok}>{saving ? "Saving…" : "Save Split"}</button>
        </div>
      </div>
    </div>
  );
}

// ── Record-receipt form ──────────────────────────────────────────────────────
function ReceiptForm({ requestId, recipients, onClose, onSaved }) {
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState("");
  const [method, setMethod] = useState("wire");
  const [reference, setReference] = useState("");
  const [recipientId, setRecipientId] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const save = () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { setErr("Enter an amount greater than 0."); return; }
    setSaving(true); setErr(null);
    apiFetch(`/reimbursements/${requestId}/receipts`, {
      method: "POST",
      body: { amount: amt, received_date: date || null, method, reference,
              recipient_id: recipientId ? Number(recipientId) : null },
    })
      .then(() => onSaved())
      .catch(e => setErr(String(e.message || e)))
      .finally(() => setSaving(false));
  };

  const lbl = { fontSize: 12, color: "var(--text2)", fontWeight: 600, display: "block", marginBottom: 4 };
  const inp = { width: "100%", padding: "7px 10px", fontSize: 13, border: "1px solid var(--border)", borderRadius: 6 };
  return (
    <div className="card" style={{ marginBottom: 14, background: "var(--bg2,#f8fafc)" }}>
      <div className="card-body" style={{ display: "grid", gap: 10 }}>
        <div className="grid-2" style={{ gap: 10 }}>
          <div><label style={lbl}>Amount received</label><input style={inp} type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" /></div>
          <div><label style={lbl}>Date received</label><input style={inp} type="date" value={date} onChange={e => setDate(e.target.value)} /></div>
        </div>
        <div className="grid-2" style={{ gap: 10 }}>
          <div><label style={lbl}>Method</label>
            <select style={inp} value={method} onChange={e => setMethod(e.target.value)}>
              <option value="wire">Wire</option><option value="check">Check</option><option value="ach">ACH</option><option value="other">Other</option>
            </select>
          </div>
          <div><label style={lbl}>Reference (check # / wire ref)</label><input style={inp} value={reference} onChange={e => setReference(e.target.value)} /></div>
        </div>
        <div><label style={lbl}>Apply to (optional)</label>
          <select style={inp} value={recipientId} onChange={e => setRecipientId(e.target.value)}>
            <option value="">Whole request</option>
            {(recipients || []).map(rc => <option key={rc.id} value={rc.id}>{rc.display_name} ({fmt.money(rc.allocated_amount)})</option>)}
          </select>
        </div>
        {err && <div style={{ color: "var(--red)", fontSize: 12 }}>{err}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="btn btn-outline btn-sm" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>{saving ? "Saving…" : "Record Receipt"}</button>
        </div>
      </div>
    </div>
  );
}
